import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown,
  Calculator,
  Target,
  AlertCircle,
  CheckCircle,
  Euro,
  Percent,
  ArrowRight
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Unternehmerlohn = () => {
  const [aktuell, setAktuell] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zielLoading, setZielLoading] = useState(false);
  
  // Ziel-Berechnung
  const [zielMonat, setZielMonat] = useState("");
  const [zielJahr, setZielJahr] = useState("");
  const [zielErgebnis, setZielErgebnis] = useState(null);

  useEffect(() => {
    fetchAktuell();
  }, []);

  const fetchAktuell = async () => {
    try {
      const res = await axios.get(`${API}/tools/unternehmerlohn`, { withCredentials: true });
      setAktuell(res.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleZielMonatChange = (value) => {
    setZielMonat(value);
    if (value) {
      setZielJahr((parseFloat(value) * 12).toFixed(0));
    } else {
      setZielJahr("");
    }
  };

  const handleZielJahrChange = (value) => {
    setZielJahr(value);
    if (value) {
      setZielMonat((parseFloat(value) / 12).toFixed(0));
    } else {
      setZielMonat("");
    }
  };

  const berechneZiel = async () => {
    if (!zielMonat && !zielJahr) return;
    
    setZielLoading(true);
    try {
      const payload = {};
      if (zielMonat) {
        payload.ziel_unternehmerlohn_monat = parseFloat(zielMonat);
      } else if (zielJahr) {
        payload.ziel_unternehmerlohn_jahr = parseFloat(zielJahr);
      }
      
      const res = await axios.post(`${API}/tools/ziel-unternehmerlohn`, payload, { withCredentials: true });
      setZielErgebnis(res.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setZielLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#858585]">Berechne...</div>
      </div>
    );
  }

  return (
    <div data-testid="unternehmerlohn-page">
      {/* Header */}
      <header className="page-header">
        <h1 className="page-title">Was verdiene ich?</h1>
        <p className="page-subtitle">Unternehmerlohn nach allen Abzügen & Steuern</p>
      </header>

      <div className="px-8 py-4">
        {/* Aktueller Stand */}
        {aktuell && (
          <>
            {/* Hauptergebnis */}
            <div className={`p-8 mb-6 ${aktuell.bewertung === 'positiv' ? 'bg-[#E8F5E9] border-[#C8E6C9]' : 'bg-[#FFEBEE] border-[#FFCDD2]'} border`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#5C5C5C] mb-2">Ihr Netto-Unternehmerlohn</p>
                  <p className={`font-mono text-4xl font-bold ${aktuell.bewertung === 'positiv' ? 'text-[#2E7D32]' : 'text-[#C62828]'}`}>
                    {formatCurrency(aktuell.unternehmerlohn.netto_monat)}
                  </p>
                  <p className="text-sm text-[#5C5C5C] mt-1">pro Monat</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-2xl font-medium text-[#3A4D39]">
                    {formatCurrency(aktuell.unternehmerlohn.netto_jahr)}
                  </p>
                  <p className="text-sm text-[#5C5C5C]">pro Jahr</p>
                  <p className="text-xs text-[#858585] mt-2">
                    ≈ {formatCurrency(aktuell.unternehmerlohn.pro_tag_bei_25_tagen)} / Tag
                  </p>
                </div>
              </div>
              <p className={`mt-4 text-sm ${aktuell.bewertung === 'positiv' ? 'text-[#1B5E20]' : 'text-[#B71C1C]'}`}>
                {aktuell.empfehlung}
              </p>
            </div>

            {/* Detailrechnung */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Einnahmen */}
              <div className="bg-white border border-stone-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp size={20} className="text-[#2E7D32]" />
                  <h3 className="font-display text-lg font-semibold">Einnahmen</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#5C5C5C]">Ø Monatsumsatz</span>
                    <span className="font-mono font-medium text-[#2E7D32]">
                      + {formatCurrency(aktuell.einnahmen.durchschnitt_monat)}
                    </span>
                  </div>
                  <p className="text-xs text-[#858585]">
                    Basierend auf {aktuell.einnahmen.monate_erfasst} erfassten Monaten
                  </p>
                </div>
              </div>

              {/* Kosten */}
              <div className="bg-white border border-stone-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingDown size={20} className="text-[#C62828]" />
                  <h3 className="font-display text-lg font-semibold">Kosten</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5C5C5C]">Fixkosten</span>
                    <span className="font-mono text-[#C62828]">- {formatCurrency(aktuell.kosten.fixkosten_monat)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5C5C5C]">Personalkosten</span>
                    <span className="font-mono text-[#C62828]">- {formatCurrency(aktuell.kosten.personalkosten_monat)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5C5C5C]">Wareneinsatz ({aktuell.kosten.wareneinsatz_quote_prozent}%)</span>
                    <span className="font-mono text-[#C62828]">- {formatCurrency(aktuell.kosten.wareneinsatz_geschaetzt)}</span>
                  </div>
                  <div className="border-t border-stone-200 pt-2 mt-2">
                    <div className="flex justify-between font-medium">
                      <span>Gesamtkosten</span>
                      <span className="font-mono text-[#C62828]">- {formatCurrency(aktuell.kosten.gesamtkosten_monat)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Gewinn & Steuern */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border border-stone-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Euro size={20} className="text-[#3A4D39]" />
                  <h3 className="font-display text-lg font-semibold">Rohgewinn</h3>
                </div>
                <p className="font-mono text-2xl font-medium">
                  {formatCurrency(aktuell.gewinn.rohgewinn_monat)}
                </p>
                <p className="text-sm text-[#858585]">vor Steuern</p>
              </div>

              <div className="bg-white border border-stone-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Percent size={20} className="text-[#D96C4A]" />
                  <h3 className="font-display text-lg font-semibold">Steuern (geschätzt)</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#5C5C5C]">Gewerbesteuer</span>
                    <span className="font-mono">- {formatCurrency(aktuell.steuern_geschaetzt.gewerbesteuer_monat)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5C5C5C]">Einkommensteuer</span>
                    <span className="font-mono">- {formatCurrency(aktuell.steuern_geschaetzt.einkommensteuer_monat)}</span>
                  </div>
                </div>
                <p className="text-xs text-[#858585] mt-3">{aktuell.steuern_geschaetzt.hinweis}</p>
              </div>
            </div>
          </>
        )}

        {/* Ziel-Berechnung */}
        <div className="bg-white border border-stone-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-[#E8F5E9] text-[#3A4D39]">
              <Target size={20} />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold text-[#3A4D39]">Ziel-Unternehmerlohn berechnen</h2>
              <p className="text-sm text-[#858585]">Was muss ich tun, um mein Wunschgehalt zu erreichen?</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#5C5C5C] mb-2 block">
                Wunsch-Nettolohn pro Monat
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  value={zielMonat}
                  onChange={(e) => handleZielMonatChange(e.target.value)}
                  placeholder="z.B. 4000"
                  className="pr-10"
                  data-testid="input-ziel-monat"
                />
                <Euro size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#858585]" />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-[#5C5C5C] mb-2 block">
                Wunsch-Nettolohn pro Jahr
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  value={zielJahr}
                  onChange={(e) => handleZielJahrChange(e.target.value)}
                  placeholder="z.B. 48000"
                  className="pr-10"
                  data-testid="input-ziel-jahr"
                />
                <Euro size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#858585]" />
              </div>
            </div>
          </div>

          <Button
            onClick={berechneZiel}
            disabled={zielLoading || (!zielMonat && !zielJahr)}
            className="w-full md:w-auto bg-[#3A4D39] hover:bg-[#2D3B2D] text-white"
            data-testid="btn-ziel-berechnen"
          >
            <Calculator size={16} className="mr-2" />
            {zielLoading ? "Berechne..." : "Weg zum Ziel berechnen"}
          </Button>
        </div>

        {/* Ziel-Ergebnis */}
        {zielErgebnis && (
          <div className="bg-[#F5F5F5] border border-stone-200 p-6">
            <h3 className="font-display text-lg font-semibold text-[#3A4D39] mb-4">
              So erreichen Sie {formatCurrency(zielErgebnis.ziel.unternehmerlohn_netto_monat)}/Monat
            </h3>

            {zielErgebnis.handlungsempfehlung.umsatz_differenz <= 0 ? (
              <div className="bg-[#E8F5E9] border border-[#C8E6C9] p-4 flex items-center gap-3">
                <CheckCircle size={24} className="text-[#2E7D32]" />
                <p className="text-[#1B5E20] font-medium">{zielErgebnis.hinweis}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white border border-stone-200 p-4">
                  <p className="text-xs uppercase tracking-wide text-[#858585] mb-2">Benötigter Monatsumsatz</p>
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-sm text-[#5C5C5C]">Aktuell</p>
                      <p className="font-mono text-lg">{formatCurrency(zielErgebnis.aktuelle_situation.umsatz_monat)}</p>
                    </div>
                    <ArrowRight size={20} className="text-[#D96C4A]" />
                    <div>
                      <p className="text-sm text-[#5C5C5C]">Benötigt</p>
                      <p className="font-mono text-lg font-medium text-[#D96C4A]">{formatCurrency(zielErgebnis.benoetigt.umsatz_monat)}</p>
                    </div>
                    <div className="ml-auto bg-[#FFF3E0] px-3 py-1 border border-[#FFE0B2]">
                      <span className="font-mono text-[#E65100]">+{zielErgebnis.handlungsempfehlung.umsatz_steigerung_prozent}%</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white border border-stone-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-[#858585] mb-1">Mehr Umsatz pro Tag</p>
                    <p className="font-mono text-xl font-medium text-[#D96C4A]">
                      +{formatCurrency(zielErgebnis.handlungsempfehlung.mehr_umsatz_pro_tag)}
                    </p>
                  </div>
                  <div className="bg-white border border-stone-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-[#858585] mb-1">Oder: Ø Preiserhöhung</p>
                    <p className="font-mono text-xl font-medium text-[#3A4D39]">
                      +{formatCurrency(zielErgebnis.handlungsempfehlung.durchschnittliche_preis_erhoehung)}
                    </p>
                    <p className="text-xs text-[#858585]">pro Produkt</p>
                  </div>
                  <div className="bg-white border border-stone-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-[#858585] mb-1">Oder: Kostensenkung</p>
                    <p className="font-mono text-xl font-medium text-[#2E7D32]">
                      -{formatCurrency(zielErgebnis.handlungsempfehlung.alternative_kostensenkung)}
                    </p>
                    <p className="text-xs text-[#858585]">pro Monat</p>
                  </div>
                </div>

                <div className="bg-[#E3F2FD] border border-[#BBDEFB] p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle size={20} className="text-[#1565C0] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#0D47A1]">
                      <strong>Tipp:</strong> Kombinieren Sie Preiserhöhungen mit mehr Verkäufen. 
                      Schon 2-3 zusätzliche Cocktails pro Tag können einen großen Unterschied machen!
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Unternehmerlohn;
